import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { GlobalStyle } from './styles/global';
import { NotificationProvider } from './contexts/NotificationContext';
import { DynamicThemeProvider } from './contexts/ThemeContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { AuthProvider } from './contexts/AuthContext';

import RouteTracker from './components/RouteTracker';
import CookieConsent from './components/CookieConsent';
import HomePage from './pages/HomePage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import PropertyMapPage from './pages/PropertyMapPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AdvertisePropertyPage from './pages/AdvertisePropertyPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import PrivacyPage from './pages/PrivacyPage';
import LocationPage from './pages/LocationPage';

import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProperties from './pages/admin/AdminProperties';
import AdminPropertyForm from './pages/admin/AdminPropertyForm';
import AdminCategories from './pages/admin/AdminCategories';
import AdminLeads from './pages/admin/AdminLeads';
import AdminBrokers from './pages/admin/AdminBrokers';
import AdminSettings from './pages/admin/AdminSettingsNew';
import AdminLogin from './pages/admin/AdminLogin';
import AdminCanalPro from './pages/admin/AdminCanalPro';
import PropertyFollowUp from './pages/admin/PropertyFollowUp';
import AdminMediaBatches from './pages/admin/AdminMediaBatches';
import AdminMediaBatchDetail from './pages/admin/AdminMediaBatchDetail';
import AdminMcpAuthorization from './pages/admin/AdminMcpAuthorization';
import AdminLocations from './pages/admin/AdminLocations';
import AdminEditorial from './pages/admin/AdminEditorial';

import SuperadminRoute from './components/SuperadminRoute';
import SuperadminLayout from './layouts/SuperadminLayout';
import SuperadminDashboard from './pages/Superadmin/Dashboard';
import SuperadminTenants from './pages/Superadmin/Tenants';
import NewTenant from './pages/Superadmin/Tenants/NewTenant';
import EditTenant from './pages/Superadmin/Tenants/EditTenant';
import TenantConfig from './pages/Superadmin/Tenants/TenantConfig';
import { isControlPlaneHost } from './utils/runtime';
import Seo from './components/Seo';

const queryClient = new QueryClient();

const RouteSeoGuard: React.FC = () => {
  const { pathname } = useLocation();
  if (!pathname.startsWith('/admin') && !pathname.startsWith('/superadmin') && pathname !== '/login') {
    return null;
  }
  return (
    <Seo
      title="Área administrativa"
      description="Área restrita para usuários autorizados."
      canonicalPath={pathname}
      robots="noindex,nofollow"
    />
  );
};

const App: React.FC = () => {
  const isControlPlane = isControlPlaneHost();

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <DynamicThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <GlobalStyle />
              <Router>
                <RouteSeoGuard />
                {!isControlPlane && <RouteTracker />}
                {!isControlPlane && <CookieConsent />}
                <Routes>
                {isControlPlane ? (
                  <>
                    <Route path="/" element={<Navigate to="/superadmin" replace />} />
                    <Route path="/login" element={<AdminLogin />} />
                    <Route path="/superadmin" element={
                      <SuperadminRoute>
                        <SuperadminLayout />
                      </SuperadminRoute>
                    }>
                      <Route index element={<SuperadminDashboard />} />
                      <Route path="tenants" element={<SuperadminTenants />} />
                      <Route path="tenants/new" element={<NewTenant />} />
                      <Route path="tenants/:tenantId/edit" element={<EditTenant />} />
                      <Route path="tenants/:tenantId/config" element={<TenantConfig />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/superadmin" replace />} />
                  </>
                ) : (
                  <>
                <Route path="/" element={<HomePage />} />
                <Route path="/properties" element={<PropertiesPage />} />
                <Route path="/imoveis" element={<PropertiesPage />} />
                <Route path="/properties/:id" element={<PropertyDetailPage />} />
                <Route path="/imovel/:id" element={<PropertyDetailPage />} />
                <Route path="/mapa" element={<PropertyMapPage />} />
                <Route path="/condominio/:slug" element={<LocationPage />} />
                <Route path="/bairro/:slug" element={<LocationPage />} />
                <Route path="/sobre" element={<AboutPage />} />
                <Route path="/contato" element={<ContactPage />} />
                <Route path="/anunciar" element={<AdvertisePropertyPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPage />} />
                <Route path="/privacy-policy" element={<PrivacyPage />} />
                
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="properties" element={<AdminProperties />} />
                  <Route path="properties/new" element={<AdminPropertyForm />} />
                  <Route path="properties/edit/:id" element={<AdminPropertyForm />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="locais" element={<AdminLocations />} />
                  <Route path="editorial" element={<AdminEditorial />} />
                  <Route path="leads" element={<AdminLeads />} />
                  <Route path="brokers" element={<AdminBrokers />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="integrations/canal-pro" element={<AdminCanalPro />} />
                  <Route path="integrations/mcp/authorize" element={<AdminMcpAuthorization />} />
                  <Route path="property-follow-up" element={<PropertyFollowUp />} />
                  <Route path="media-batches" element={<AdminMediaBatches />} />
                  <Route path="media-batches/:id" element={<AdminMediaBatchDetail />} />
                </Route>
                <Route path="/superadmin/*" element={<Navigate to="/admin" replace />} />

                  </>
                )}
              </Routes>
            </Router>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
            </NotificationProvider>
          </AuthProvider>
        </DynamicThemeProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
};

export default App; 
